import { Reducer } from 'react';
import { negate } from 'lodash/fp';
import { LicenseAction } from '../../../../../types/__generated__/graphql';

export interface LicenseUpdate {
  productFamilyId: number;
  productCurriculumId: number;
  action: LicenseAction;
}

export enum ActionType {
  ADD_UPDATES = 'ADD_UPDATES'
}

interface AddUpdatesAction {
  type: ActionType.ADD_UPDATES;
  payload: {
    action: LicenseAction;
    productFamilyId: number;
    productCurriculumIds: number[];
  };
}

type Action = AddUpdatesAction;

const isSameCurriculum = (first: LicenseUpdate) => (second: LicenseUpdate) => {
  return (
    first.productFamilyId === second.productFamilyId &&
    first.productCurriculumId === second.productCurriculumId
  );
};

const isEqual = (first: LicenseUpdate) => (second: LicenseUpdate) =>
  isSameCurriculum(first)(second) && first.action === second.action;

const addUpdate = (existingUpdates: LicenseUpdate[], newUpdate: LicenseUpdate) => {
  if (existingUpdates.some(isEqual(newUpdate))) return existingUpdates;

  const existing = existingUpdates.find(isSameCurriculum(newUpdate));

  // Nothing matching existing curriculum, add it.
  if (!existing) return [...existingUpdates, newUpdate];

  // Same curriculum, different action so remove it.
  return existingUpdates.filter(negate(isSameCurriculum(existing)));
};

const reducer: Reducer<LicenseUpdate[], Action> = (
  state: LicenseUpdate[],
  { type, payload }: Action
) => {
  switch (type) {
    case ActionType.ADD_UPDATES: {
      const { action, productFamilyId, productCurriculumIds } = payload;
      const updates = productCurriculumIds.map(productCurriculumId => ({
        action,
        productFamilyId,
        productCurriculumId
      }));

      return updates.reduce(addUpdate, state);
    }
    default: {
      return state;
    }
  }
};

export default reducer;
